import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import colours from 'theme/atoms/colours';
import { isTouchDevice } from 'shared/helpers/device';
import { useSignalStatistics } from 'dashboard/hooks/useSignalStatistics';
import {
  BAR_SIZE,
  BarShape,
  CustomCursor,
} from 'dashboard/components/HorizontalBarShapes';

import * as RechartsStyled from 'dashboard/styles/Recharts.style';
import * as Styled from 'dashboard/styles/Overview.style';
import * as Text from 'theme/components/Text.style';
import BehaviourTooltip from './BehaviourTooltip';

const BORDER_PADDING = 10;
const X_AXIS_HEIGHT = 30;
const ROW_HEIGHT = 40;
const MIN_CHART_HEIGHT = 200;

const CHART_STYLE = {
  fontFamily: 'Inter, sans-serif',
  WebkitFontSmoothing: 'antialiased',
  MozOsxFontSmoothing: 'grayscale',
} as const;

interface BehavioursSectionProps {
  queryParams: string;
}

export default function BehavioursSection({
  queryParams,
}: BehavioursSectionProps): JSX.Element | null {
  const { t } = useTranslation('pageDashboard');
  const { data, isSuccess } = useSignalStatistics(queryParams);

  if (!isSuccess || data.length === 0) return null;

  const chartHeight = Math.max(MIN_CHART_HEIGHT, data.length * ROW_HEIGHT);

  return (
    <Styled.SectionContainer>
      <Styled.SectionHeader>
        <Text.H5>{t('behaviours.title')}</Text.H5>
      </Styled.SectionHeader>

      <Styled.Card>
        <RechartsStyled.ChartContainer>
          <RechartsStyled.HorizontalBarLayout>
            <RechartsStyled.HorizontalBarLabels
              $paddingTop={BORDER_PADDING}
              $paddingBottom={BORDER_PADDING}
            >
              {data.map((item) => (
                <RechartsStyled.HorizontalBarLabel key={item.signal_key}>
                  {item.signal}
                </RechartsStyled.HorizontalBarLabel>
              ))}
            </RechartsStyled.HorizontalBarLabels>

            <RechartsStyled.ChartBorder>
              <ResponsiveContainer width="100%" height={chartHeight}>
                <BarChart
                  data={data}
                  layout="vertical"
                  margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                  style={CHART_STYLE}
                >
                  <YAxis type="category" dataKey="signal" hide />
                  <XAxis type="number" allowDecimals={false} hide />
                  <CartesianGrid
                    horizontal={false}
                    stroke={colours.neutral[100]}
                  />
                  <Tooltip
                    trigger={isTouchDevice() ? 'click' : 'hover'}
                    content={<BehaviourTooltip />}
                    cursor={<CustomCursor />}
                  />
                  <Bar
                    dataKey="count"
                    fill={colours.blue[500]}
                    barSize={BAR_SIZE}
                    shape={<BarShape />}
                  />
                </BarChart>
              </ResponsiveContainer>
            </RechartsStyled.ChartBorder>

            <div
              style={{ gridColumn: 2, padding: `0 ${BORDER_PADDING + 1}px` }}
            >
              <ResponsiveContainer width="100%" height={X_AXIS_HEIGHT}>
                <BarChart data={data} layout="vertical" style={CHART_STYLE}>
                  <YAxis type="category" dataKey="signal" hide />
                  <XAxis
                    type="number"
                    allowDecimals={false}
                    tickLine={false}
                    axisLine={false}
                    tick={{
                      fill: colours.neutral[900],
                      fontSize: 13,
                      fontWeight: 650,
                    }}
                    padding={{ right: 15 }}
                  />
                  <Bar dataKey="count" fill="transparent" barSize={0} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </RechartsStyled.HorizontalBarLayout>
        </RechartsStyled.ChartContainer>
      </Styled.Card>
    </Styled.SectionContainer>
  );
}
